import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

export default function EmailVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const stateEmail = location.state?.email;
    
    if (stateEmail) {
      setEmail(stateEmail);
    } else {
      navigate('/signup');
    }
  }, [location, navigate]);

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/verify-email', {
        email: email,
        code: code
      });
      
      toast.success('Email verified successfully!');
      navigate(location.state?.returnTo || '/profile-setup');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await api.post('/auth/resend-verification', { email });
      toast.success('New verification code sent to your email!');
    } catch (error) {
      toast.error('Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="app-container gradient-bg">
      <div className="px-4 py-8 min-h-screen flex flex-col items-center justify-center">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4 pulse-animation">
            <Mail className="text-primary" size={40} />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Verify Your Email</h1>
          <p className="text-gray-400 text-sm">
            We've sent a 6-digit code to<br />
            <span className="text-white font-medium">{email}</span>
          </p>
        </div>

        {/* Info Box */}
        <div className="mb-6 glass p-4 rounded-xl border border-primary/30 max-w-md w-full">
          <p className="text-gray-300 text-sm text-center">
            Check your email inbox (and spam folder) for the verification code from <span className="text-primary font-medium">noreply@blindmatch.in</span>
          </p>
        </div>

        <form onSubmit={handleVerify} className="w-full max-w-md space-y-6">
          {/* Code Input */}
          <div>
            <label className="text-sm font-medium text-gray-400 mb-1.5 block text-center">Enter Verification Code</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full bg-secondary/50 border border-white/10 rounded-xl h-14 px-4 text-white text-center text-2xl font-bold placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none tracking-widest"
              placeholder="000000"
              maxLength={6}
              data-testid="verification-code-input"
            />
          </div>

          {/* Verify Button */}
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold rounded-full transition-all duration-300 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="verify-button"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                Verifying...
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                Verify Email
              </>
            )}
          </button>
        </form>

        {/* Resend */}
        <div className="mt-6 text-center">
          <p className="text-gray-400 text-sm mb-2">Didn't receive the code?</p>
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-primary hover:text-primary/80 font-medium text-sm disabled:opacity-50"
            data-testid="resend-button"
          >
            {resending ? 'Sending...' : 'Resend Code'}
          </button>
        </div>

        {/* Skip - keeps people moving if the email is slow or lands in spam */}
        <div className="mt-8 text-center">
          <button
            onClick={() => {
              toast.info("No problem — you can verify your email anytime from your dashboard.");
              navigate(location.state?.returnTo || '/profile-setup');
            }}
            className="text-gray-400 hover:text-white text-sm underline underline-offset-4"
            data-testid="skip-verification-button"
          >
            Verify later, continue to my profile
          </button>
          <p className="text-gray-500 text-xs mt-2">
            Tip: signing in with Google skips this step entirely.
          </p>
        </div>
      </div>
    </div>
  );
}
